import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to detect if a media query matches
 */
export const useMediaQuery = (query: string): boolean => {
  const getMatches = useCallback((): boolean => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  }, [query]);

  const [matches, setMatches] = useState<boolean>(getMatches);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    
    const handleChange = () => {
      setMatches(mediaQueryList.matches);
    };

    // Set initial value
    setMatches(mediaQueryList.matches);

    // Add listener
    mediaQueryList.addEventListener('change', handleChange);

    return () => {
      mediaQueryList.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
};

/**
 * Hook to detect if we're on a desktop-sized screen (1024px and up)
 */
export const useIsDesktop = (): boolean => {
  return useMediaQuery('(min-width: 1024px)');
};

/**
 * Hook to detect if we're on a tablet-sized screen (768px to 1023px)
 */
export const useIsTablet = (): boolean => {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
};

/**
 * Hook to detect if we're on a mobile-sized screen (below 768px)
 */
export const useIsMobile = (): boolean => {
  return useMediaQuery('(max-width: 767px)');
};

/**
 * Hook that returns current breakpoint
 */
export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

export const useBreakpoint = (): Breakpoint => {
  const isDesktop = useIsDesktop();
  const isTablet = useIsTablet();
  
  if (isDesktop) return 'desktop';
  if (isTablet) return 'tablet';
  return 'mobile';
};
